import { MouseEventHandler, useEffect, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, MotionConfig } from 'motion/react'
import QRCode from 'react-qr-code'
import SVG from 'react-inlinesvg'
import { backdropVariants, cardVariants, qrCodeVariants } from './dialogAnimation'

type QRCodeItem = { value: string; label?: string }

interface Props {
  title: string
  description: string
  qrCodes: QRCodeItem[]
  onClose: () => void
}

// the address without the protocol, so visitors can tell where the code leads before scanning it
const toDisplayUrl = (value: string) => value.replace(/^https?:\/\/(www\.)?/, '')

// A dialog pointing visitors to pages they can't open on the kiosk, one QR code per link.
// Rendered inside AnimatePresence, so it stays until the card has animated out
const QRCodeDialog = ({ title, description, qrCodes, onClose }: Props) => {
  const { t } = useTranslation()
  const titleId = useId()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleClickOutside: MouseEventHandler<HTMLDivElement> = e => {
    if (e.currentTarget !== e.target) return
    onClose()
  }

  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-[6px]"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={handleClickOutside}
      >
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="relative flex w-full max-w-[760px] flex-col gap-8 rounded-[28px] bg-white p-8 shadow-floating xs:flex-row-reverse xs:items-center xs:gap-12 xs:p-12"
          variants={cardVariants}
        >
          <button
            type="button"
            aria-label={t('qrCodeDialog.close')}
            className="absolute right-5 top-5 flex size-12 cursor-pointer items-center justify-center rounded-full border-0 bg-black95 p-0 text-white [transition:background-color_0.2s] hover:bg-red"
            onClick={onClose}
          >
            <SVG className="size-[18px]" src="/icons/x.svg" />
          </button>
          <div className="flex flex-1 flex-col gap-4 pr-12 xs:pr-6">
            <h2
              id={titleId}
              className="m-0 font-approach-regular text-[26px] leading-[1.25] tracking-[0.5px] text-black"
            >
              {title}
            </h2>
            <p className="m-0 whitespace-pre-wrap font-approach-light text-[18px] leading-[1.6] tracking-[0.3px] text-black70">
              {description}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap justify-center gap-6">
            {qrCodes.map(qrCode => (
              <motion.figure
                key={qrCode.value}
                className="m-0 flex flex-col items-center gap-3"
                variants={qrCodeVariants}
              >
                <div className="rounded-[20px] bg-white p-4 ring-1 ring-black/10">
                  <QRCode size={200} value={qrCode.value} />
                </div>
                <figcaption className="flex flex-col items-center gap-1 text-center">
                  {qrCode.label ? (
                    <span className="font-approach-regular text-[15px] text-black">
                      {qrCode.label}
                    </span>
                  ) : null}
                  <span className="font-approach-light text-[14px] tracking-[0.3px] text-black49">
                    {toDisplayUrl(qrCode.value)}
                  </span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </MotionConfig>
  )
}

export default QRCodeDialog
